import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { AccessTime as TimeIcon, Restaurant as PortionsIcon } from '@mui/icons-material';
import { ReactComponent as Logo } from 'images/logo.svg';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import RecipeIllustration from 'components/nutrition/RecipeIllustration';
import { DEFAULT_RECIPE_COLOR } from 'components/nutrition/recipeColors';

// Tarjeta de receta autocontenida (objeto "receta impresa"): se ve igual en claro/oscuro porque es
// una pieza con su propia superficie clara, como la referencia. El color de acento lo elige el
// creador (paleta curada) y tiñe etiqueta, cabeceras de columna y la banda de pie.
const CARD_BG = '#FBFAF7';
const INK = '#2A2A2A';
const INK_SOFT = '#5B5B5B';

function RecipeCardTemplate({ recipe }) {
  const { t } = useTranslation(['nutrition']);
  const accent = recipe.accent_color || DEFAULT_RECIPE_COLOR;
  const ingredients = recipe.ingredients ?? [];
  const steps = recipe.steps ?? [];

  // La tarjeta es una pieza de superficie CLARA fija; el HTML de BlockNote usa tokens del tema
  // (texto blanco en oscuro) → se fuerza tinta oscura con !important para que siempre sea legible.
  const proseWrap = {
    color: INK,
    '& p, & li, & h1, & h2, & h3, & h4, & h5, & h6, & strong, & em, & span, & td, & figcaption, & blockquote, & .bn-inline-content, & div': { color: `${INK} !important` },
    // Marcadores de lista de BlockNote (full HTML) = pseudo ::before que heredan el color del tema
    // (blanco en oscuro). En la card crema hay que forzarlos a tinta oscura, igual que la lista nativa.
    '& [data-content-type="bulletListItem"]::before, & [data-content-type="numberedListItem"]::before, & [data-content-type="checkListItem"]::before, & li::marker, & summary::before': { color: `${INK} !important` },
    '& h2, & h3, & h4': { color: `${accent} !important` },
    '& a': { color: `${accent} !important` },
    '& th': { backgroundColor: 'rgba(0,0,0,0.05) !important', color: `${INK} !important` },
    '& th, & td': { borderColor: 'rgba(0,0,0,0.20) !important' },
    '& blockquote': { backgroundColor: 'rgba(0,0,0,0.04) !important', borderColor: `${accent} !important` },
    '& code, & pre': { backgroundColor: 'rgba(0,0,0,0.05) !important' },
    '& img': { borderColor: 'rgba(0,0,0,0.12) !important' },
    '& hr': { borderColor: 'rgba(0,0,0,0.15) !important' },
    fontSize: 16,
    lineHeight: 1.6,
    '& p, & li, & .bn-inline-content': { fontSize: '15.5px !important', lineHeight: 1.6 },
    '& li': { marginTop: '0.45em', marginBottom: '0.45em' }
  };

  return (
    <Box sx={{ borderRadius: '18px', overflow: 'hidden', backgroundColor: CARD_BG, boxShadow: '0 12px 32px rgba(0,0,0,0.28)' }}>
      {/* Foto superior */}
      {recipe.image_url ? (
        <Box component="img" src={recipe.image_url} alt={recipe.name} sx={{ width: '100%', height: 250, objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ width: '100%', height: 170, backgroundColor: accent, opacity: 0.9 }} />
      )}

      {/* Cuerpo */}
      <Box
        sx={{
          position: 'relative',
          px: { xs: 3, sm: 5 },
          pt: 4,
          pb: 4,
          // Imagen de fondo opcional: backdrop muy tenue detrás del contenido.
          ...(recipe.background_image_url && {
            backgroundImage: `linear-gradient(${CARD_BG}E6, ${CARD_BG}E6), url(${recipe.background_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          })
        }}
      >
        <Logo style={{ width: 56, opacity: 0.05, position: 'absolute', right: 14, bottom: 14, pointerEvents: 'none', color: INK }} />

        {recipe.meal_label && (
          <Typography sx={{ textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.24em', fontSize: 13, fontWeight: 700, color: accent, mb: 0.75 }}>
            {recipe.meal_label}
          </Typography>
        )}
        <Typography sx={{ textAlign: 'center', fontWeight: 800, fontSize: { xs: 27, sm: 34 }, color: INK, lineHeight: 1.15, mb: 2 }}>
          {recipe.name}
        </Typography>

        {/* Fila tiempo + porciones */}
        {(recipe.prep_time != null || recipe.portions != null) && (
          <Stack direction="row" spacing={4} justifyContent="center" alignItems="center" sx={{ mb: 3.5, color: INK_SOFT }}>
            {recipe.prep_time != null && recipe.prep_time !== '' && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TimeIcon sx={{ fontSize: 22, color: accent }} />
                <Typography sx={{ color: INK_SOFT, fontSize: 15 }}>
                  {t('prepMinutes', { ns: 'nutrition', defaultValue: '{{n}} min', n: recipe.prep_time })}
                </Typography>
              </Stack>
            )}
            {recipe.portions != null && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PortionsIcon sx={{ fontSize: 22, color: accent }} />
                <Typography sx={{ color: INK_SOFT, fontSize: 15 }}>
                  {t('portionsCount', { ns: 'nutrition', defaultValue: 'Porciones: {{n}}', n: recipe.portions })}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {/* Dos columnas (contenido enriquecido BlockNote; fallback a la lista simple legada).
            Separación amplia + divisor punteado entre Ingredientes y Preparación, como la referencia. */}
        <Grid container spacing={{ xs: 3, sm: 6 }}>
          <Grid xs={12} sm={6}>
            <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 800, fontSize: 15, color: accent, mb: 1.25 }}>
              {t('ingredients', { ns: 'nutrition', defaultValue: 'Ingredientes' })}
            </Typography>
            {recipe.ingredients_html ? (
              <Box sx={proseWrap}><ContentRenderer value={recipe.ingredients_html} /></Box>
            ) : (
              <Box component="ul" sx={{ pl: 2.4, m: 0, '& li': { mb: 0.7, color: INK, fontSize: 15.5 } }}>
                {ingredients.map((ing) => <li key={ing}>{ing}</li>)}
              </Box>
            )}
          </Grid>
          <Grid xs={12} sm={6} sx={{ borderLeft: { sm: '1px dashed' }, borderColor: { sm: 'rgba(0,0,0,0.18)' } }}>
            <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 800, fontSize: 15, color: accent, mb: 1.25 }}>
              {t('steps', { ns: 'nutrition', defaultValue: 'Preparación' })}
            </Typography>
            {recipe.steps_html ? (
              <Box sx={proseWrap}><ContentRenderer value={recipe.steps_html} /></Box>
            ) : (
              <Box component="ol" sx={{ pl: 2.4, m: 0, '& li': { mb: 0.7, color: INK, fontSize: 15.5 } }}>
                {/* eslint-disable-next-line react/no-array-index-key */}
                {steps.map((step, i) => <li key={`step-${i}`}>{step}</li>)}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Ilustración decorativa (estilo dibujado), como la olla del ejemplo de referencia. */}
        {recipe.illustration && (
          <Box sx={{ mt: 3, color: accent, opacity: 0.9 }}>
            <RecipeIllustration name={recipe.illustration} size={68} />
          </Box>
        )}
      </Box>

      {/* Banda de pie */}
      {recipe.footer_note && (
        <Box sx={{ backgroundColor: accent, py: 1.6, px: 2 }}>
          <Typography sx={{ textAlign: 'center', color: '#FFFFFF', fontSize: 14, fontWeight: 600 }}>
            {recipe.footer_note}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

RecipeCardTemplate.propTypes = { recipe: PropTypes.object.isRequired };

export default RecipeCardTemplate;
